import React from 'react';
import { Box, TableRow, TableCell, Typography, IconButton, Tooltip } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { getCapturedImageDisplayUrl } from '../../../services/s3/captureUrlService';

/**
 * DetectionEventRow - Renders a detection event (no match found)
 *
 * Displays when faces are detected but no matches were found.
 */
function DetectionEventRowInner({
  event,
  isChild = false,
  address,
  onLocationClick
}) {
  const [showEnlarged, setShowEnlarged] = React.useState(false);
  const canvasRef = React.useRef(null);
  const imageRef = React.useRef(null);

  const capturedImageUrl = getCapturedImageDisplayUrl(event.metadata.capturedImageUrl);
  const timestamp = new Date(event.timestamp || event.createdAt);
  const faceCount = event.metadata?.detectedFaceCount || 0;
  const allFaceDetails = event.metadata?.allFaceDetails || [];

  // Get color for processing elapsed time
  const getElapsedTimeColor = (ms) => {
    if (ms < 500) return '#4caf50'; // Green
    if (ms < 750) return '#ff9800'; // Amber
    if (ms >= 1500) return '#f44336'; // Red
    return '#ff9800'; // Amber for 750-1499ms range
  };

  // Determine background color based on relationship
  const backgroundColor = isChild ? '#f5f5f5' : '#fff';
  const borderColor = isChild ? '4px solid #e0e0e0' : 'none';

  // Handle captured image click - toggle enlarged view
  const handleCapturedImageClick = React.useCallback(() => {
    setShowEnlarged(prev => !prev);
  }, []);

  // Draw bounding boxes on canvas when image loads
  const drawImageWithBoundingBoxes = React.useCallback(() => {
    if (!canvasRef.current || !imageRef.current) return;

    const canvas = canvasRef.current;
    const img = imageRef.current;

    // Wait for image to be loaded
    if (!img.complete || img.naturalWidth === 0) return;

    const ctx = canvas.getContext('2d');

    // Set canvas size to match image natural dimensions
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    // Draw the image
    ctx.drawImage(img, 0, 0);

    // Draw bounding boxes for all detected faces
    allFaceDetails.forEach((face) => {
      const boundingBox = face.BoundingBox;
      if (!boundingBox) return;

      const { Width, Height, Left, Top } = boundingBox;
      const boxX = Left * img.naturalWidth;
      const boxY = Top * img.naturalHeight;
      const boxWidth = Width * img.naturalWidth;
      const boxHeight = Height * img.naturalHeight;

      // Draw rectangle
      ctx.strokeStyle = '#ff9800'; // Orange for detection (different from match blue)
      ctx.lineWidth = 3;
      ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);

      // Draw corner indicators
      const cornerSize = 15;
      ctx.fillStyle = '#ff9800';

      // Top-left
      ctx.fillRect(boxX - 1.5, boxY - 1.5, cornerSize, 3);
      ctx.fillRect(boxX - 1.5, boxY - 1.5, 3, cornerSize);

      // Top-right
      ctx.fillRect(boxX + boxWidth - cornerSize + 1.5, boxY - 1.5, cornerSize, 3);
      ctx.fillRect(boxX + boxWidth - 1.5, boxY - 1.5, 3, cornerSize);

      // Bottom-left
      ctx.fillRect(boxX - 1.5, boxY + boxHeight - 1.5, cornerSize, 3);
      ctx.fillRect(boxX - 1.5, boxY + boxHeight - cornerSize + 1.5, 3, cornerSize);

      // Bottom-right
      ctx.fillRect(boxX + boxWidth - cornerSize + 1.5, boxY + boxHeight - 1.5, cornerSize, 3);
      ctx.fillRect(boxX + boxWidth - 1.5, boxY + boxHeight - cornerSize + 1.5, 3, cornerSize);
    });
  }, [allFaceDetails]);

  // Trigger drawing when enlarged view opens or image changes
  React.useEffect(() => {
    if (showEnlarged && capturedImageUrl) {
      // Small delay to ensure image is loaded
      const timer = setTimeout(drawImageWithBoundingBoxes, 50);
      return () => clearTimeout(timer);
    }
  }, [showEnlarged, capturedImageUrl, drawImageWithBoundingBoxes]);

  return (
    <>
      <TableRow
        hover
        sx={{
          backgroundColor,
          borderLeft: borderColor,
          '& .MuiTableCell-root': { borderBottom: '1px solid rgba(224, 224, 224, 0.4)' },
          '&:hover': {
            backgroundColor,
            filter: 'brightness(0.98)'
          }
        }}
      >
        {/* Event Type Icon */}
        <TableCell align="center" sx={{ width: 40 }}>
          <Tooltip title="Detection (No Match)">
            <VisibilityIcon sx={{ color: 'text.secondary', fontSize: 28 }} />
          </Tooltip>
        </TableCell>

        {/* Expand/Collapse Column (empty for detections) */}
        <TableCell align="center" sx={{ width: 40 }} />

        {/* Event Details */}
        <TableCell sx={{ py: 1.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 64 }}>
              {/* Captured Image Thumbnail */}
              {capturedImageUrl && (
                <Box
                  component="img"
                  src={capturedImageUrl}
                  alt="Captured"
                  onClick={handleCapturedImageClick}
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 1,
                    objectFit: 'cover',
                    border: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.8
                    }
                  }}
                />
              )}

              {/* Event Description */}
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {event.description || `Detected ${faceCount} face${faceCount !== 1 ? 's' : ''}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {event.userHandle || event.userEmail || event.userId}
                </Typography>
              </Box>
            </Box>

            {/* Enlarged image with bounding boxes */}
            {showEnlarged && capturedImageUrl && (
              <Box sx={{ width: '100%', mt: 1 }}>
                {/* Hidden image for loading */}
                <img
                  ref={imageRef}
                  src={capturedImageUrl}
                  alt="Reference for canvas"
                  style={{ display: 'none' }}
                  onLoad={drawImageWithBoundingBoxes}
                />

                {/* Canvas with bounding boxes */}
                <canvas
                  ref={canvasRef}
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                    border: '2px solid rgba(255, 152, 0, 0.5)',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    display: 'block'
                  }}
                />
              </Box>
            )}
          </Box>
        </TableCell>

        {/* Timestamp */}
        <TableCell sx={{ width: 140 }}>
          <Typography variant="caption">
            {timestamp.toLocaleString()}
          </Typography>
          {event.metadata?.processingElapsedMs !== undefined && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontSize: '0.7rem',
                color: getElapsedTimeColor(event.metadata.processingElapsedMs),
                fontWeight: 500
              }}
            >
              +{event.metadata.processingElapsedMs}ms
            </Typography>
          )}
        </TableCell>

        {/* User Account */}
        <TableCell sx={{ width: 120 }}>
          <Typography variant="caption" color="text.secondary">
            {event.userHandle || event.userEmail || event.userId || '—'}
          </Typography>
        </TableCell>

        {/* Location */}
        <TableCell>
          {event.location ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onLocationClick?.(event);
                }}
                sx={{ p: 0.5 }}
              >
                <LocationOnIcon fontSize="small" color="primary" />
              </IconButton>
              <Typography variant="caption">
                {address || `${event.location.latitude.toFixed(4)}, ${event.location.longitude.toFixed(4)}`}
              </Typography>
            </Box>
          ) : (
            <Typography variant="caption">N/A</Typography>
          )}
        </TableCell>
      </TableRow>
    </>
  );
}

// Wrapper component that validates metadata before rendering
const DetectionEventRow = React.memo(function DetectionEventRow(props) {
  const hasValidMetadata = props.event.metadata &&
    typeof props.event.metadata === 'object' &&
    !props.event.metadata.S &&
    !props.event.metadata.M &&
    !props.event.metadata.NULL;

  if (!hasValidMetadata) {
    return null;
  }

  return <DetectionEventRowInner {...props} />;
});

export default DetectionEventRow;
